/**
 * @file index.tsx
 *
 * @fileoverview Renders a badge using the styles provided in the theme.
 */

import { cva } from "class-variance-authority";
import React from "react";
import styles from "./badge.module.scss";

const badge = cva([styles.badge], {
  variants: {
    badgeStyle: {
      primary: styles.primary,
      primaryLight: styles.primaryLight,
      primaryVeryLight: styles.primaryVeryLight,
      secondary: styles.secondary,
      secondaryLight: styles.secondaryLight,
      secondaryVeryLight: styles.secondaryVeryLight,
      default: styles.default,
      success: styles.success,
      successLight: styles.successLight,
      successVeryLight: styles.successVeryLight,
      danger: styles.danger,
      dangerLight: styles.dangerLight,
      dangerVeryLight: styles.dangerVeryLight,
      warning: styles.warning,
      warningLight: styles.warningLight,
      warningVeryLight: styles.warningVeryLight,
      featured: styles.featured,
      featuredLight: styles.featuredLight,
      featuredVeryLight: styles.featuredVeryLight,
      topJob: styles.topJob,
    },
  },
  defaultVariants: {
    badgeStyle: "default",
  },
});

export type BadgeStyleType =
  | "primary"
  | "primaryLight"
  | "primaryVeryLight"
  | "secondary"
  | "secondaryLight"
  | "secondaryVeryLight"
  | "default"
  | "success"
  | "successLight"
  | "successVeryLight"
  | "danger"
  | "dangerLight"
  | "dangerVeryLight"
  | "warning"
  | "warningLight"
  | "warningVeryLight"
  | "featured"
  | "featuredLight"
  | "featuredVeryLight"
  | "topJob";

export type BadgeProps = React.HTMLAttributes<any> & {
  /**
   * The content to be displayed inside the badge.
   *
   * @default "null"
   */
  children: React.ReactNode;
  /**
   * The style for the badge. The color of the badge element will be changed accordingly.
   *
   * @default "default"
   */
  badgeStyle?: BadgeStyleType;
  /**
   * The theme props is passed automatically to the component via styled components.
   *
   * @default null
   */
  theme?: any;
  /**
   * The font size.
   */
  fontSize?: string;
};

/**
 * Badges contain numeric values, like the number of unread messages.
 * Use badges to label page items with a small amount of information that help.
 * Badges are normally placed before or after the label of the thing they're quantifying
 * and contain on.
 */
export const Badge = ({
  children,
  badgeStyle = "primary",
  className,
  ...props
}: BadgeProps) => {
  return (
    <div
      className={`${badge({ badgeStyle })} ${className}`}
      data-testid="styled-badge"
      {...props}
    >
      {children}
    </div>
  );
};

export default Badge;
